import { create } from 'zustand'
import { useBetSliderStore } from './betSliderStore'
import { NavigationMessages, UltraPlayModalStateChangedMessage } from 'game-ui-types'

export const ULTRA_PLAY_SPIN_OPTIONS = [10, 20, 30, 40, 50, 70, 100, 500, 1000, -1]

// Special Buy Bonus dropdown option (games with Double Chance): behaves like a buy bonus feature in
// the dropdown but drives the engine's Double Chance toggle instead of a buy bonus purchase.
export const ULTRA_PLAY_DOUBLE_CHANCE = 'DOUBLE_CHANCE'

export type UltraPlayStatus = 'READY' | 'PLAYING' | 'PAUSED'
export type UltraPlaySpinType = 'NORMAL' | 'FREE_SPIN' | 'CASCADE' | 'BONUS'

export type UltraPlaySubSpin = {
    win: number
    winMultiplier: number
    balance: number
    type: UltraPlaySpinType
    freeSpinsAwarded?: number
}

export type UltraPlaySpin = {
    spinNumber: number
    bet: number
    win: number
    winMultiplier: number
    balance: number
    type: UltraPlaySpinType
    freeSpinsAwarded?: number
    subSpins?: UltraPlaySubSpin[]
    isExpanded: boolean
}

type UltraPlayStore = {
    isOpen: boolean
    status: UltraPlayStatus
    selectedBetIndex: number
    selectedSpinsIndex: number
    remainingSpins: number
    currentSpinNumber: number
    totalBet: number
    totalWin: number
    startingBalance: number
    lastBalance: number
    spins: UltraPlaySpin[]
    stopOnFeature: boolean
    selectedBuyBonusFeatureName: string
    showFeatureChoiceDialog: boolean
    featureDialogAutoplayable: boolean
    isInFeature: boolean
    previousSpeedLevel: number
    pendingAutoplayRestart: boolean
    showInsufficientBalance: boolean
    // Sticky: the game lost its connection (engine shows "no connection" in the info line and never
    // recovers without a reload). While set, Ultra Play blocks play. Not cleared by reset/open/close.
    isNoConnection: boolean
    // True from the moment Pause is clicked (COLLECT_CLICKED + STOP_AUTOPLAY_CLICKED sent) until the
    // engine actually confirms the stop (SPIN_BUTTON_AUTOPLAY_STOPPED). Locks the controls meanwhile —
    // clicking Play before that confirmation arrives races a new AUTOPLAY_BUTTON_CLICKED against the
    // still-in-flight stop, and the recorder ends up attributing events from two overlapping batches to
    // the same pending bet, corrupting rows.
    isPauseInFlight: boolean
    isBetInFlight: boolean
    isPausePending: boolean
    hasUnrecordedBet: boolean
    currentFeatureName: string

    open: () => void
    close: () => void
    setStatus: (status: UltraPlayStatus) => void
    setSelectedBetIndex: (index: number) => void
    setSelectedSpinsIndex: (index: number) => void
    setStopOnFeature: (stop: boolean) => void
    setSelectedBuyBonusFeatureName: (name: string) => void
    addSpin: (spin: Omit<UltraPlaySpin, 'isExpanded'>, expanded?: boolean) => void
    addSubSpin: (spinNumber: number, subSpin: UltraPlaySubSpin) => void
    addFeatureRetrigger: (parentSpinNumber: number, extraFreeSpins: number) => void
    updateSpin: (spinNumber: number, partial: Partial<UltraPlaySpin>) => void
    setSpinExpanded: (spinNumber: number, expanded: boolean) => void
    start: (startingBalance: number, remainingSpins: number) => void
    // Resume a finished session: keep the table, totals and spin counter; just add more spins.
    continueSession: (remainingSpins: number) => void
    pause: () => void
    resume: () => void
    stop: () => void
    setShowFeatureChoiceDialog: (show: boolean) => void
    setShowInsufficientBalance: (show: boolean) => void
    setNoConnection: (value: boolean) => void
    setFeatureDialogAutoplayable: (autoplayable: boolean) => void
    setIsInFeature: (inFeature: boolean) => void
    toggleSpinExpanded: (spinNumber: number) => void
    decrementRemainingSpins: () => void
    extendRemainingSpins: (extraSpins: number) => void
    reset: () => void
    setPreviousSpeedLevel: (level: number) => void
    setPendingAutoplayRestart: (v: boolean) => void
    setPauseInFlight: (v: boolean) => void
    setBetInFlight: (v: boolean) => void
    setPausePending: (v: boolean) => void
    setHasUnrecordedBet: (v: boolean) => void
    setCurrentFeatureName: (name: string) => void
}

export const useUltraPlayStore = create<UltraPlayStore>((set) => ({
    isOpen: false,
    status: 'READY',
    selectedBetIndex: 0,
    selectedSpinsIndex: 1,
    remainingSpins: 0,
    currentSpinNumber: 0,
    totalBet: 0,
    totalWin: 0,
    startingBalance: 0,
    lastBalance: 0,
    spins: [],
    stopOnFeature: false,
    selectedBuyBonusFeatureName: 'OFF',
    showFeatureChoiceDialog: false,
    featureDialogAutoplayable: false,
    isInFeature: false,
    previousSpeedLevel: 0,
    pendingAutoplayRestart: false,
    showInsufficientBalance: false,
    isNoConnection: false,
    isPauseInFlight: false,
    isBetInFlight: false,
    isPausePending: false,
    hasUnrecordedBet: false,
    currentFeatureName: '',

    // Reset both selections on open: bet from the live bet slider, spins to the default — so reopening
    // Ultra Play always starts from a clean state (previously spins kept the old selection).
    open: () =>
        set({
            isOpen: true,
            selectedBetIndex: useBetSliderStore.getState().activeIndex ?? 0,
            selectedSpinsIndex: 1,
            currentFeatureName: '',
        }),
    close: () => set({ isOpen: false }),
    setStatus: (status) => set({ status }),
    setSelectedBetIndex: (index) => set({ selectedBetIndex: index }),
    setSelectedSpinsIndex: (index) => set({ selectedSpinsIndex: index }),
    setStopOnFeature: (stopOnFeature) => set({ stopOnFeature }),
    setSelectedBuyBonusFeatureName: (name) => set({ selectedBuyBonusFeatureName: name }),
    addSpin: (spin, expanded = false) =>
        set((state) => ({
            spins: [{ ...spin, isExpanded: expanded }, ...state.spins],
            currentSpinNumber: spin.spinNumber,
            totalBet: state.totalBet + spin.bet,
            totalWin: state.totalWin + spin.win,
            lastBalance: spin.balance,
        })),
    addSubSpin: (spinNumber, subSpin) =>
        set((state) => ({
            spins: state.spins.map((s) =>
                s.spinNumber === spinNumber ? { ...s, subSpins: [...(s.subSpins ?? []), subSpin] } : s
            ),
            totalWin: state.totalWin + subSpin.win,
            lastBalance: subSpin.balance,
        })),
    addFeatureRetrigger: (parentSpinNumber, extraFreeSpins) =>
        set((state) => ({
            spins: state.spins.map((spin) => {
                if (spin.spinNumber !== parentSpinNumber) return spin

                const subSpins = spin.subSpins

                if (subSpins && subSpins.length > 0) {
                    return {
                        ...spin,
                        subSpins: subSpins.map((subSpin, i) =>
                            i === subSpins.length - 1
                                ? { ...subSpin, freeSpinsAwarded: (subSpin.freeSpinsAwarded ?? 0) + extraFreeSpins }
                                : subSpin
                        ),
                    }
                }

                return spin
            }),
        })),
    updateSpin: (spinNumber, partial) =>
        set((state) => ({
            spins: state.spins.map((s) => (s.spinNumber === spinNumber ? { ...s, ...partial } : s)),
        })),
    setSpinExpanded: (spinNumber, expanded) =>
        set((state) => ({
            spins: state.spins.map((s) => (s.spinNumber === spinNumber ? { ...s, isExpanded: expanded } : s)),
        })),
    start: (startingBalance, remainingSpins) =>
        set({
            status: 'PLAYING',
            startingBalance,
            lastBalance: startingBalance,
            remainingSpins,
            currentSpinNumber: 0,
            totalBet: 0,
            totalWin: 0,
            spins: [],
            isInFeature: false,
            showInsufficientBalance: false,
            isBetInFlight: false,
            isPausePending: false,
            hasUnrecordedBet: false,
        }),
    continueSession: (remainingSpins) =>
        set({
            status: 'PLAYING',
            remainingSpins,
            isInFeature: false,
            showInsufficientBalance: false,
            isBetInFlight: false,
            isPausePending: false,
        }),
    pause: () => set({ status: 'PAUSED', isBetInFlight: false, isPausePending: false }),
    resume: () => set({ status: 'PLAYING', isBetInFlight: false, isPausePending: false }),
    stop: () =>
        set({
            status: 'READY',
            remainingSpins: 0,
            isInFeature: false,
            isPauseInFlight: false,
            isBetInFlight: false,
            isPausePending: false,
        }),
    setShowFeatureChoiceDialog: (show) => set({ showFeatureChoiceDialog: show }),
    setShowInsufficientBalance: (show) => set({ showInsufficientBalance: show }),
    setNoConnection: (value) => set({ isNoConnection: value }),
    setFeatureDialogAutoplayable: (autoplayable) => set({ featureDialogAutoplayable: autoplayable }),
    setIsInFeature: (inFeature) => set({ isInFeature: inFeature }),
    toggleSpinExpanded: (spinNumber) =>
        set((state) => ({
            spins: state.spins.map((s) => (s.spinNumber === spinNumber ? { ...s, isExpanded: !s.isExpanded } : s)),
        })),
    decrementRemainingSpins: () =>
        // -1 marks infinite spins — keep it so the session never hits the "0 = target reached" stop.
        set((state) => ({
            remainingSpins: state.remainingSpins === -1 ? -1 : Math.max(0, state.remainingSpins - 1),
        })),
    extendRemainingSpins: (extraSpins) => set((state) => ({ remainingSpins: state.remainingSpins + extraSpins })),
    reset: () =>
        set({
            status: 'READY',
            remainingSpins: 0,
            currentSpinNumber: 0,
            totalBet: 0,
            totalWin: 0,
            lastBalance: 0,
            // Clear so the setup screen shows the live balance on reopen, not the previous session's start.
            startingBalance: 0,
            spins: [],
            showFeatureChoiceDialog: false,
            featureDialogAutoplayable: false,
            isInFeature: false,
            selectedBuyBonusFeatureName: 'OFF',
            showInsufficientBalance: false,
            isPauseInFlight: false,
            isBetInFlight: false,
            isPausePending: false,
            hasUnrecordedBet: false,
        }),
    setPreviousSpeedLevel: (level) => set({ previousSpeedLevel: level }),
    setPendingAutoplayRestart: (v) => set({ pendingAutoplayRestart: v }),
    setPauseInFlight: (v) => set({ isPauseInFlight: v }),
    setBetInFlight: (v) => set({ isBetInFlight: v }),
    setPausePending: (v) => set({ isPausePending: v }),
    setHasUnrecordedBet: (v) => set({ hasUnrecordedBet: v }),
    setCurrentFeatureName: (name) => set({ currentFeatureName: name }),
}))

useUltraPlayStore.subscribe((state, prevState) => {
    if (state.isOpen === prevState.isOpen) {
        return
    }

    const status = state.isOpen ? 'opened' : 'closed'
    const message: UltraPlayModalStateChangedMessage = {
        type: NavigationMessages.ULTRA_PLAY_MODAL_STATE_CHANGED,
        payload: { status },
    }

    window.postMessage(message)
})
